import React, { useState } from "react";
import Checkbox from "../../../components/common/Checkbox";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import ActionButtons from "../../../components/PageUI/ActionButtons";
import PreviewFile from "../../../components/common/PreviewFile";
import {
  userRequestButtonsConfig,
  userRequestStatusVariants,
  userRequestStatusMessages,
} from "../constants/userRequestConstants";
import {
  InboxInIcon,
  UserIcon,
  CalendarIcon,
  ChevronDownIcon,
  TagIcon,
  DocumentTextIcon,
  PhotographIcon,
  InformationCircleIcon,
  PhoneIcon,
  MailIcon,
} from "@heroicons/react/outline";

const UnifiedUserRequestCard = ({
  request,
  handleSelectRequest,
  handleSingleAction,
  handleDeleteRequest,
  selected,
  expandedByDefault = false,
}) => {
  // State management
  const [isExpanded, setIsExpanded] = useState(expandedByDefault);
  const [showAllMedia, setShowAllMedia] = useState(false);

  // Set initial expanded state
  React.useEffect(() => {
    setIsExpanded(expandedByDefault);
  }, [expandedByDefault]);

  // Toggle expanded view
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Format request data for display
  const formatRequestData = (data) => {
    const formatValue = (value) => {
      if (typeof value === "object" && value !== null) {
        return JSON.stringify(value, null, 2);
      }
      return value;
    };

    return Object.entries(data).map(([key, value]) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1),
      value: formatValue(value),
    }));
  };

  // Get display request data with password masking if needed
  const getDisplayRequestData = () => {
    if (request.requestType === "passwordReset") {
      return formatRequestData(
        Object.keys(request.requestData).reduce((acc, key) => {
          acc[key] =
            key === "newPassword" ? "********" : request.requestData[key];
          return acc;
        }, {})
      );
    }
    return formatRequestData(request.requestData);
  };

  // Handle action button clicks
  const handleAction = (action) => {
    if (action === "delete") {
      if (window.confirm("Are you sure you want to delete this request?")) {
        handleDeleteRequest(request._id);
      }
    } else {
      handleSingleAction(request._id, action);
    }
  };

  // Get action buttons based on status
  const renderActionButtons = () => {
    const config =
      typeof userRequestButtonsConfig[
        request.status.charAt(0).toUpperCase() + request.status.slice(1)
      ] === "function"
        ? userRequestButtonsConfig[
            request.status.charAt(0).toUpperCase() + request.status.slice(1)
          ](request.requestType)
        : userRequestButtonsConfig[
            request.status.charAt(0).toUpperCase() + request.status.slice(1)
          ] || userRequestButtonsConfig.All;

    return (
      <ActionButtons
        buttonsConfig={config}
        onAction={handleAction}
        size="sm"
        className="space-x-1"
      />
    );
  };

  // Format date for display
  const formattedDate = new Date(request.createdAt).toLocaleDateString(
    "en-US",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );

  return (
    <div
      className={`bg-white border rounded-lg shadow-sm transition-all   duration-200 ${
        selected
          ? "border-primary-light ring-1 shadow-lg shadow-primary ring-primary"
          : "border-gray-200"
      } w-full mb-4`}
    >
      {/* Entire Card Header is clickable for expanding/collapsing */}
      <div
        className={`cursor-pointer rounded-t-lg shadow-sm hover:shadow-md transition-shadow duration-200 ${isExpanded ? "bg-gray-50" : "bg-white"}`}
        onClick={toggleExpanded}
        role="button"
        aria-expanded={isExpanded}
        aria-controls={`request-details-${request._id}`}
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && toggleExpanded()}
      >
        {/* Card Header Content - Always visible */}
        <div className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 flex-wrap">
            {/* Left side - Request info, responsive design */}
            <div
              className="flex items-center gap-3 min-w-0"
              onClick={(e) => e.stopPropagation()} // Prevent expanding when clicking checkbox area
            >
              {/* Checkbox - larger touch target */}
              <div className="flex items-center justify-center w-8 h-8">
                <Checkbox
                  label=""
                  name={`select-${request._id}`}
                  checked={selected}
                  onChange={() => handleSelectRequest(request._id)}
                  className="h-5 w-5"
                />
              </div>

              {/* Request details with improved hierarchy */}
              <div className="flex flex-col min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {/* Request ID with better visual emphasis */}
                  <h2 className="text-lg font-bold text-gray-900">
                    {request.requestId}
                  </h2>

                  {/* Status badge - more prominent */}
                  <Badge
                    variant={
                      userRequestStatusVariants[request.status.toLowerCase()] ||
                      "default"
                    }
                    className="text-xs font-medium py-1"
                  >
                    {userRequestStatusMessages[request.status.toLowerCase()] ||
                      request.status}
                  </Badge>
                </div>

                {/* Customer name */}
                <div className="text-sm text-gray-800 font-medium mt-1 truncate max-w-xs">
                  {request.userId.name}
                </div>

                {/* Request date with improved iconography */}
                <div className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                  <CalendarIcon className="w-3.5 h-3.5 text-gray-500" />
                  <span>{formattedDate}</span>
                </div>
              </div>
            </div>

            {/* Middle section - Expansion indicator */}
            <div
              className="order-last sm:order-none flex items-center justify-center sm:mx-2 my-1 sm:my-0"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded();
              }}
            >
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors ${
                  isExpanded ? "bg-gray-100" : ""
                } text-sm text-gray-600`}
              >
                <div
                  className={`w-5 h-5 flex items-center justify-center rounded-full bg-gray-200 transition-transform duration-300 ${
                    isExpanded ? "rotate-180" : ""
                  }`}
                >
                  <ChevronDownIcon className="w-3.5 h-3.5" />
                </div>
                <span className="font-medium whitespace-nowrap">
                  {isExpanded ? "Hide details" : "Show details"}
                </span>

                {/* Progress bar */}
                <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
                  <div
                    className={`h-full bg-primary transition-all duration-300 ease-in-out ${
                      isExpanded ? "w-full" : "w-0"
                    }`}
                  ></div>
                </div>
              </div>
            </div>

            {/* Right side - Request type and actions */}
            <div
              className="flex flex-wrap items-center gap-2 justify-start sm:justify-end"
              onClick={(e) => e.stopPropagation()} // Prevent expanding when clicking action buttons
            >
              {/* Request type pill */}
              <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full text-sm font-medium text-gray-700">
                <InboxInIcon className="w-3.5 h-3.5 text-gray-500" />
                <span>{request.requestType}</span>
              </div>

              {/* Action buttons - more accessible */}
              <div className="ml-1">{renderActionButtons()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Collapsible content */}
      <div
        id={`request-details-${request._id}`}
        className={`transition-all rounded-t-lg duration-300 ease-in-out overflow-hidden ${
          isExpanded ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        {/* Display Request Details in a grid */}
        <div className="p-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 1) Customer Information */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2">
                <UserIcon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-gray-800">
                  Customer Information
                </h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div>
                  <p className="font-medium text-base">{request.userId.name}</p>
                  <div className="flex items-center gap-1 text-gray-600 mt-1">
                    <MailIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span>{request.userId.email}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600 mt-1">
                    <PhoneIcon className="w-3.5 h-3.5 text-gray-500" />
                    <span>{request.userId.phoneNumber}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2) Request Details */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2">
                <DocumentTextIcon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-gray-800">Request Details</h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="grid grid-cols-1 gap-3">
                  {getDisplayRequestData().map((item, index) => (
                    <div key={index} className="text-sm">
                      <span className="text-gray-500 block text-xs">
                        {item.label}
                      </span>
                      <span className="font-medium text-gray-800 block break-words">
                        {item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 3) Related Information (Product/Category) */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2">
                <InformationCircleIcon className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-gray-800">
                  Related Information
                </h3>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                {request.product && (
                  <div className="mb-3">
                    <p className="text-gray-500 text-xs mb-1">Product</p>
                    <p className="font-medium text-gray-800">
                      {request.product.name}
                    </p>

                    {request.product.description && (
                      <div className="mt-2">
                        <p className="text-gray-500 text-xs mb-1">
                          Description
                        </p>
                        <p className="text-sm text-gray-700">
                          {request.product.description}
                        </p>
                      </div>
                    )}

                    {/* Product Media */}
                    {request.product.media &&
                      request.product.media.length > 0 && (
                        <div className="mt-3">
                          <p className="text-gray-500 text-xs mb-1">
                            Media ({request.product.media.length})
                          </p>
                          <div className="flex space-x-2 overflow-x-auto py-2">
                            {(showAllMedia
                              ? request.product.media
                              : request.product.media.slice(0, 2)
                            ).map((media, index) => (
                              <div
                                key={index}
                                className="w-20 h-20 flex-shrink-0"
                              >
                                <PreviewFile
                                  preview={media}
                                  index={index}
                                  handleRemoveFile={null}
                                />
                              </div>
                            ))}

                            {request.product.media.length > 2 && (
                              <Button
                                variant="outline"
                                onClick={() => setShowAllMedia(!showAllMedia)}
                                className="h-10 flex items-center justify-center text-primary border border-dashed rounded-lg px-2 flex-shrink-0"
                              >
                                {showAllMedia
                                  ? "Show Less"
                                  : `+${request.product.media.length - 2} More`}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                )}

                {request.category && (
                  <div>
                    <p className="text-gray-500 text-xs mb-1">Category</p>
                    <p className="font-medium text-gray-800">
                      {request.category.name}
                    </p>
                  </div>
                )}

                {!request.product && !request.category && (
                  <p className="text-gray-600">
                    No related product or category information
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Summary (visible when collapsed) */}
      <div
        className={`bg-gray-50 grid grid-cols-1 rounded-b-lg sm:grid-cols-3 transition-all duration-300 ${
          isExpanded
            ? "max-h-0 p-0 gap-0 overflow-hidden border-t-0 opacity-0 invisible"
            : "max-h-[200px] p-3 gap-3 opacity-100 visible border-t border-gray-200"
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="bg-primary-light/20 p-2 rounded-full">
            <UserIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="text-sm">
            <p className="text-gray-500">Customer</p>
            <p className="font-medium">
              {request.userId.name}, {request.userId.email}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-primary-light/20 p-2 rounded-full">
            <TagIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="text-sm">
            <p className="text-gray-500">Request Type</p>
            <p className="font-medium">
              {request.requestType}
              {request.product ? ` • ${request.product.name}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-primary-light/20 p-2 rounded-full">
            <InformationCircleIcon className="w-4 h-4 text-primary" />
          </div>
          <div className="text-sm">
            <p className="text-gray-500">Status</p>
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  userRequestStatusVariants[request.status.toLowerCase()] ||
                  "default"
                }
                className="mt-1 text-xs"
              >
                {userRequestStatusMessages[request.status.toLowerCase()] ||
                  request.status}
              </Badge>
              <span className="text-gray-600">{formattedDate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedUserRequestCard;
